import interstellarApi from './interstellarApi';

export interface CallGroupUser {
  call_group_id: number;
  user_id: number;
  created_at?: string | null;
  modified_at?: string | null;
}

export interface CallGroup {
  id: number;
  account_id: number;
  name: string;
  participants: CallGroupUser[];
  created_at?: string | null;
  modified_at?: string | null;
}

/** Raw shape from API — participants may be omitted */
interface CallGroupRaw extends Omit<CallGroup, 'participants'> {
  participants?: CallGroupUser[];
}

interface CallGroupsListResponse {
  total_count: number;
  page: number;
  page_size: number;
  call_groups: CallGroupRaw[];
}

export interface CreateCallGroupPayload {
  name: string;
  users: number[];
}

interface CreateCallGroupResponse {
  message: string;
  id: number;
}

/**
 * Lista grupos de llamada de la cuenta: `GET /incoming-call-config/group`
 */
export const fetchCallGroups = async (
  accountId: number,
  page = 1,
  pageSize = 200
): Promise<CallGroup[]> => {
  const response = await interstellarApi.get<CallGroupsListResponse>(
    '/incoming-call-config/group',
    {
      params: {
        account_id: accountId,
        page,
        page_size: pageSize,
      },
    }
  );
  return (response.data.call_groups ?? []).map((g) => ({
    ...g,
    participants: g.participants ?? [],
  }));
};

/**
 * Obtiene detalle de un grupo: `GET /incoming-call-config/group/{groupId}`
 */
export const fetchCallGroup = async (groupId: number): Promise<CallGroup> => {
  const response = await interstellarApi.get<CallGroupRaw>(
    `/incoming-call-config/group/${groupId}`
  );
  return { ...response.data, participants: response.data.participants ?? [] };
};

/**
 * Crea un nuevo grupo de llamada: `POST /incoming-call-config/group`
 */
export const createCallGroup = async (
  accountId: number,
  payload: CreateCallGroupPayload
): Promise<CreateCallGroupResponse> => {
  const response = await interstellarApi.post<CreateCallGroupResponse>(
    '/incoming-call-config/group',
    payload,
    { params: { account_id: accountId } }
  );
  return response.data;
};

/**
 * Actualiza un grupo existente: `PUT /incoming-call-config/group/{groupId}`
 */
export const updateCallGroup = async (
  groupId: number,
  payload: CreateCallGroupPayload
): Promise<{ message: string; id: number }> => {
  const response = await interstellarApi.put<{ message: string; id: number }>(
    `/incoming-call-config/group/${groupId}`,
    payload
  );
  return response.data;
};

/**
 * Elimina un grupo: `DELETE /incoming-call-config/group/{groupId}`
 */
export const deleteCallGroup = async (groupId: number): Promise<void> => {
  await interstellarApi.delete(`/incoming-call-config/group/${groupId}`);
};
